import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { BannerNotification, Button, Text } from '@frontend/design-system';
import { LocationPortalData, PortalDisabledTypes } from '../../types';
import { LocationInformation } from '../header';

const styles = {
  pageContainer: css`
    display: flex;
    flex-flow: column;
    width: 100%;
    height: 100%;
    justify-content: space-between;
  `,
  portalContainer: css`
    display: flex;
    flex-flow: column;
    padding: ${theme.spacing(2)};
    margin: 0 auto;
    width: 100%;
    @media (min-width: ${breakpoints.small.min}px) {
      width: 544px;
      position: relative;
      height: max-content;
      top: ${theme.spacing(4.5)};
    }
  `,
  portalHeader: css`
    display: flex;
    flex-flow: column;
    margin-bottom: ${theme.spacing(2)};
  `,
};

interface PortalDisabledPageProps {
  locationData?: LocationPortalData;
  reason?: PortalDisabledTypes;
}

export const PortalDisabledPage = ({ locationData, reason }: PortalDisabledPageProps) => {
  const { t } = useTranslation('payments');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });

  const rateLimitMessaging = t('You have exceeded the allowed number of payment attempts');
  const obpDisabledMessaging = t('Our Online Bill Pay service is temporarily unavailable');

  const getBannerMessaging = () => {
    switch (reason) {
      case PortalDisabledTypes.RateLimit:
        return rateLimitMessaging;
      case PortalDisabledTypes.Disabled:
        return obpDisabledMessaging;
      default:
        return obpDisabledMessaging;
    }
  };

  return (
    <div css={styles.pageContainer}>
      <div css={styles.portalContainer}>
        <BannerNotification
          status='error'
          message={getBannerMessaging()}
          css={css`
            margin-bottom: ${theme.spacing(2)};
          `}
        />
        <div css={styles.portalHeader}>
          {locationData && (
            <LocationInformation
              locationData={{
                name: locationData?.name,
                email: locationData?.email,
                phoneNumber: locationData?.phoneNumber,
                logo: locationData?.logo,
              }}
            />
          )}
        </div>
        <Text>
          {t(
            `To pay your bill please send us a text message at ${formatPhoneNumber(
              locationData?.phoneNumber,
              true,
              true
            )} with the payment amount you would like to make, so we can send you a Text to Pay request. Alternatively, you can call us to make a payment.`
          )}
        </Text>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: start;
            width: max-content;
            gap: ${theme.spacing(2)};
          `}
        >
          {isMobile && (
            <Button
              variant='primary'
              onClick={() => (window.location.href = `sms:${locationData?.phoneNumber}`)}
              css={css`
                white-space: nowrap;
              `}
              iconName='message'
            >
              {t('Text Us')}
            </Button>
          )}
          <Button
            variant='primary'
            onClick={() => (window.location.href = `tel:${locationData?.phoneNumber}`)}
            iconName='phone'
          >
            {t('Call Us')}
          </Button>
        </div>
      </div>
    </div>
  );
};
